// =============================================================================
// MeuDriver — useSubscription
// Hook para gerenciar a assinatura Premium do aluno.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { StudentSubscriptionRow } from '@/types/database'
import { toast } from 'sonner'

const subscriptionKeys = {
  student: (userId: string) => ['subscription', 'student', userId] as const,
}

async function fetchStudentSubscription(
  userId: string,
): Promise<StudentSubscriptionRow | null> {
  const { data, error } = await supabase
    .from('student_subscriptions')
    .select('*')
    .eq('student_id', userId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data as StudentSubscriptionRow | null
}

/** Retorna a assinatura Premium ativa do aluno autenticado, ou null. */
export function useSubscription() {
  const { user } = useAuth()

  return useQuery<StudentSubscriptionRow | null, Error>({
    queryKey: subscriptionKeys.student(user?.id ?? ''),
    queryFn: () => fetchStudentSubscription(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
  })
}

/** Inicia o fluxo de assinatura Premium (redireciona para o Mercado Pago). */
export function useCreateSubscription() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<{ init_point: string }, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ init_point: string }>(
        'create-subscription',
        { body: { plan: 'premium' } },
      )
      if (error) throw new Error(error.message)
      if (!data?.init_point) throw new Error('init_point ausente na resposta')
      return data
    },
    onSuccess: (data) => {
      window.location.href = data.init_point
    },
    onError: (err) => {
      console.error('[useCreateSubscription]', err)
      toast.error('Erro ao iniciar assinatura. Tente novamente.')
    },
    onSettled: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.student(user.id) })
      }
    },
  })
}

/** Cancela a assinatura Premium do aluno. */
export function useCancelSubscription() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('cancel-subscription', { body: {} })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      toast.success('Assinatura cancelada com sucesso.')
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.student(user.id) })
      }
    },
    onError: (err) => {
      console.error('[useCancelSubscription]', err)
      toast.error('Erro ao cancelar assinatura. Tente novamente.')
    },
  })
}
